import { Button, Chip, Group } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';

import { AccessDenied } from 'src/Components/AccessDenied';
import { PageHeader } from 'src/Components/Navigation/PageHeader';
import { TaskDataTable } from 'src/Components/Tasks/TaskDataTable';
import useDeleteTask from 'src/Hooks/Mutations/useDeleteTask';
import useUpdateTask from 'src/Hooks/Mutations/useUpdateTask';
import useFetchTasks from 'src/Hooks/Queries/useFetchTasks';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { useCanCreate, useCanRead } from 'src/Hooks/Shared/useHasPermission';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import { usePermissionDeniedModal } from 'src/Hooks/Shared/usePermissionDeniedModal';
import { TaskDto, TaskStatus } from 'src/types/task.types';

import styles from './TasksMainPage.module.scss';

const TasksMainPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const assetId = useCurrentAssetId();
  const { data: tasks, isLoading } = useFetchTasks(assetId ?? '');
  const { mutate: updateTask } = useUpdateTask(assetId ?? '');
  const { mutate: deleteTask } = useDeleteTask(assetId ?? '');
  const { data: me } = useFetchUser();
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [createdByMe, setCreatedByMe] = useState(false);
  const [onSiteOnly, setOnSiteOnly] = useState(false);

  // Permission checks
  const { hasPermission: canRead, ownerName } = useCanRead('task');
  const { hasPermission: canCreate } = useCanCreate('task');
  const showPermissionDenied = usePermissionDeniedModal(ownerName);

  const assetAboveQuotas = useIsAssetAboveQuotas();

  const filteredTasks = useMemo(() => {
    if (!tasks) return [];
    return tasks.filter(task => {
      if (assignedToMe && task.assignedTo?.id !== me?.id) return false;
      if (createdByMe && task.createdBy?.id !== me?.id) return false;
      if (onSiteOnly && !task.requiresOnSite) return false;
      return true;
    });
  }, [tasks, assignedToMe, createdByMe, onSiteOnly, me?.id]);

  const handleStatusChange = (taskId: string, newStatus: TaskStatus) => {
    updateTask({
      taskId,
      data: { status: newStatus },
    });
  };

  const handleTaskClick = (task: TaskDto) => {
    navigate(`${task.id}`);
  };

  const handleEditClick = (task: TaskDto) => {
    navigate(`${task.id}/edit`);
  };

  const handleDelete = (taskId: string) => {
    deleteTask(taskId);
  };

  // Show access denied if user doesn't have read permission
  if (!canRead) {
    return <AccessDenied ownerName={ownerName} />;
  }

  return (
    <div className={styles.container}>
      <PageHeader
        label={t('tasks.title', 'Tâches')}
        rightElement={
          <div className={styles.topActions}>
            {canCreate && (
              <Button
                onClick={() => navigate('new')}
                leftSection={<IconPlus size={20} />}
                disabled={assetAboveQuotas}
              >
                {t('tasks.addTask', 'Ajouter une tâche')}
              </Button>
            )}
          </div>
        }
      />
      <Group gap="sm" className={styles.filters}>
        <Chip checked={assignedToMe} onChange={setAssignedToMe} variant="outline">
          {t('tasks.filters.assignedToMe', 'Assignées à moi')}
        </Chip>
        <Chip checked={createdByMe} onChange={setCreatedByMe} variant="outline">
          {t('tasks.filters.createdByMe', 'Créées par moi')}
        </Chip>
        <Chip checked={onSiteOnly} onChange={setOnSiteOnly} variant="outline">
          {t('tasks.filters.onSite', 'À faire sur place')}
        </Chip>
      </Group>
      <div className={styles.cardContainer}>
        <TaskDataTable
          tasks={filteredTasks}
          fetching={isLoading}
          onTaskClick={handleTaskClick}
          onEditClick={handleEditClick}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
          canDelete={canCreate}
          onPermissionDenied={showPermissionDenied}
        />
      </div>
      <Outlet />
    </div>
  );
};

export default TasksMainPage;
